/**
 * 노드 속성 영역의 **위치(오른쪽/아래)·크기·접힘** — 보는 사람마다 따로, 브라우저에 기억한다.
 *
 * 넓은 모니터에서는 아래 배치가 가로로 긴 워크플로(DFD 흐름)를 보기 좋고, 노트북에서는 오른쪽이 낫다.
 * 어느 한쪽으로 고정하면 한쪽 사용자가 불편하므로 고르게 한다(현장 2026-09-22, 검토안 C).
 *
 * 레이아웃(ModernLayout)과 패널 머리(RightSidePanel)가 같은 값을 봐야 해서 작은 공유 저장소로 둔다.
 * 저장은 편의일 뿐이다 — 브라우저 저장소를 못 쓰면(사생활 보호 창 등) 기본값으로 돌면 된다.
 */
import { useSyncExternalStore } from 'react';

export type DockPosition = 'right' | 'bottom';

export interface DockPrefs {
  position: DockPosition;
  /** 오른쪽일 때 폭(px) */
  width: number;
  /** 아래일 때 높이(px) */
  height: number;
  collapsed: boolean;
}

const KEY = 'openflow.propertyDock';
export const DOCK_LIMITS = { minWidth: 320, maxWidth: 760, minHeight: 160, maxHeightRatio: 0.7 };
const DEFAULTS: DockPrefs = { position: 'right', width: 400, height: 280, collapsed: false };

function load(): DockPrefs {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const v = JSON.parse(raw);
    return {
      position: v.position === 'bottom' ? 'bottom' : 'right',
      width: Number.isFinite(v.width) ? v.width : DEFAULTS.width,
      height: Number.isFinite(v.height) ? v.height : DEFAULTS.height,
      collapsed: !!v.collapsed,
    };
  } catch {
    return DEFAULTS;
  }
}

let prefs: DockPrefs = load();
const listeners = new Set<() => void>();

export function setDockPrefs(patch: Partial<DockPrefs>): void {
  prefs = { ...prefs, ...patch };
  try { window.localStorage.setItem(KEY, JSON.stringify(prefs)); } catch { /* 기억 못 해도 지금 화면은 그대로 */ }
  listeners.forEach((l) => l());
}

export function useDockPrefs(): DockPrefs {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => prefs,
    () => prefs,
  );
}
